"use client";

/**
 * SafetyStatsLive — public content-safety figures for /web-safety.
 *
 * Aggregate totals only. The blocked domains themselves are never exposed: a
 * public list of confirmed adult and malware hosts is precisely the thing not
 * to publish, and it would also show anyone exactly what we do and don't catch.
 */

import { useEffect, useState } from "react";
import { Layers, ShieldCheck, ShieldX, UserCheck } from "lucide-react";
import { StatTileGrid } from "@/components/marketing/stat-tile";

const URL = "/api/index/safety";

type Safety = {
  enabled?: boolean;
  blocked_total?: number;
  blocked_by_category?: Record<string, number>;
  questionable_total?: number;
  reference_domains?: number;
  categories?: number;
  allow_overrides?: number;
};

const compact = (n?: number) => {
  if (n == null || !isFinite(n)) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  return n.toLocaleString();
};

const LABEL: Record<string, string> = {
  porn: "Adult",
  gambling: "Gambling",
  drugs: "Illegal drugs",
  malware: "Malware",
  phishing: "Phishing",
  scam: "Scams",
  piracy: "Piracy",
  unsafe: "Other",
};

export function SafetyStatsLive({ initial }: { initial?: Safety | null }) {
  /* Seeded by the server so the figure is in the HTML on first paint. Before this the value
     arrived only after hydration, so every visitor saw a dash first and anything that does not
     run JavaScript — crawlers, link previews — never saw it at all. The client fetch below is
     kept: it refreshes a page that may have been cached for minutes. */
  const [d, setD] = useState<Safety | null>(initial ?? null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let on = true;
    const load = async () => {
      try {
        const r = await fetch(URL, { cache: "no-store" });
        if (!r.ok) return;
        const j = (await r.json()) as Safety;
        if (on && j) {
          setD(j);
          setLive(true);
        }
      } catch {
        /* transient — keep what's on screen */
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      on = false;
      clearInterval(t);
    };
  }, []);

  const cats = Object.entries(d?.blocked_by_category || {}).sort((a, b) => b[1] - a[1]);

  const tiles = [
    { icon: Layers, value: compact(d?.reference_domains), label: "Reference domains", sub: "checked against, refreshed daily" },
    { icon: ShieldX, value: compact(d?.blocked_total), label: "Kept out", sub: "refused before indexing" },
    { icon: ShieldCheck, value: compact(d?.questionable_total), label: "Hidden by default", sub: "indexed, out of normal results" },
    { icon: UserCheck, value: compact(d?.allow_overrides), label: "Human overrides", sub: "wrong calls corrected" },
  ];

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <span
          className={`inline-block h-2 w-2 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-neutral-400"}`}
          aria-hidden
        />
        <span className="kicker">{live ? "Live · from the running system" : "Connecting…"}</span>
      </div>

      <StatTileGrid items={tiles} />

      {cats.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          {cats.map(([cat, n]) => (
            <span
              key={cat}
              className="inline-flex items-center gap-2 rounded-full border hairline px-3 py-1.5 text-[13px] text-foreground/70"
            >
              {LABEL[cat] || cat}
              <span className="tabular-num font-semibold text-foreground/90">{n.toLocaleString()}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
